import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    ExclamationCircleFilled,
    ClockCircleOutlined,
    DollarOutlined,
    WarningOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    Card,
    Table,
    Tag,
    Space,
    Button,
    Modal,
    Form,
    InputNumber,
    Row,
    Col,
    Statistic,
    Alert,
    Tooltip,
    Input
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../variables/api/axiosInstance';
import SideBar from '../../../components/SideBar';
import NavBar from '../../../components/NavBar';
import { Footer } from 'antd/es/layout/layout';
import { LOGGED_USER } from '../../../variables/constants';

const { confirm } = Modal;
const { TextArea } = Input;

const PenalitesCession = () => {
    const [penalites, setPenalites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPenalite, setEditingPenalite] = useState(null);
    const [form] = Form.useForm();

    const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

    useEffect(() => {
        chargerPenalites();
    }, []);

    const chargerPenalites = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/parametre-penalite');
            setPenalites(response.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des pénalités");
        } finally {
            setLoading(false);
        }
    };

    const ouvrirModalCreation = () => {
        setEditingPenalite(null);
        form.resetFields();
        setModalVisible(true);
    };

    const ouvrirModalEdition = (penalite) => {
        setEditingPenalite(penalite);
        form.setFieldsValue({
            joursDebut: penalite.joursDebut,
            joursFin: penalite.joursFin,
            montantParJour: penalite.montantParJour,
            description: penalite.description
        });
        setModalVisible(true);
    };

    const supprimerPenalite = (penalite) => {
        confirm({
            title: 'Confirmer la suppression',
            icon: <ExclamationCircleFilled />,
            content: `Voulez-vous vraiment supprimer la tranche ${penalite.joursDebut}-${penalite.joursFin} jours ?`,
            okText: 'Supprimer',
            okType: 'danger',
            cancelText: 'Annuler',
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/api/parametre-penalite/${penalite.penaliteId}`);
                    toast.success("Pénalité supprimée");
                    chargerPenalites();
                } catch (err) {
                    console.error(err);
                    toast.error("Erreur lors de la suppression");
                }
            },
        });
    };

    const validerFormulaire = async (values) => {
        try {
            const penaliteData = {
                ...values,
                penaliteId: editingPenalite?.penaliteId
            };

            if (editingPenalite) {
                await axiosInstance.put('/api/parametre-penalite', penaliteData);
                toast.success("Pénalité modifiée");
            } else {
                await axiosInstance.post('/api/parametre-penalite', penaliteData);
                toast.success("Pénalité créée");
            }

            setModalVisible(false);
            form.resetFields();
            chargerPenalites();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement");
        }
    };

    const calculerStatistiques = () => {
        const totalTranches = penalites.length;
        const montantMax = penalites.length > 0
            ? Math.max(...penalites.map(p => p.montantParJour))
            : 0;
        const montantMin = penalites.length > 0
            ? Math.min(...penalites.map(p => p.montantParJour))
            : 0;
        const joursMax = penalites.length > 0
            ? Math.max(...penalites.map(p => p.joursFin))
            : 0;

        return { totalTranches, montantMax, montantMin, joursMax };
    };

    const colonnes = [
        {
            title: 'Tranche (Jours)',
            key: 'tranche',
            render: (_, record) => (
                <div>
                    <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        <ClockCircleOutlined /> {record.joursDebut} - {record.joursFin} jours
                    </Tag>
                </div>
            ),
            sorter: (a, b) => a.joursDebut - b.joursDebut,
        },
        {
            title: 'Montant par jour',
            dataIndex: 'montantParJour',
            key: 'montantParJour',
            render: (montant) => (
                <div>
                    {/* <DollarOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} /> */}
                    <span style={{ fontWeight: 600, fontSize: '16px', color: '#ff4d4f' }}>
                        {montant?.toLocaleString('fr-FR')} FCFA
                    </span>
                </div>
            ),
            align: 'right',
            sorter: (a, b) => a.montantParJour - b.montantParJour,
        },
        {
            title: 'Pénalité maximale',
            key: 'penaliteMax',
            render: (_, record) => {
                const duree = record.joursFin - record.joursDebut;
                const penaliteMax = record.montantParJour * duree;
                return (
                    <Tooltip title={`Pour ${duree} jours de retard`}>
                        <span style={{ fontWeight: 500, color: '#faad14' }}>
                            {penaliteMax.toLocaleString('fr-FR')} FCFA
                        </span>
                    </Tooltip>
                );
            },
            align: 'right',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => text || <span style={{ color: '#999', fontStyle: 'italic' }}>Aucune description</span>,
            ellipsis: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Modifier">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => ouvrirModalEdition(record)}
                            disabled={!isPoolUser}
                        />
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => supprimerPenalite(record)}
                            disabled={!isPoolUser}
                        />
                    </Tooltip>
                </Space>
            ),
            align: 'center',
            fixed: 'right',
            width: 120,
        },
    ];

    const stats = calculerStatistiques();

    return (
        <div className="layout-wrapper layout-content-navbar">
            <style>{`
                .penalites-header-card {
                    background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
                    border-radius: 16px;
                    padding: 32px;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 20px rgba(255, 77, 79, 0.25);
                    color: white;
                }

                .penalites-header-card h2 {
                    color: white;
                    font-size: 28px;
                    font-weight: 700;
                    margin: 0 0 8px 0;
                }

                .stats-card {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    margin-bottom: 24px;
                }

                .action-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    margin-bottom: 24px;
                }
            `}</style>

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f5f5f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div className="penalites-header-card">
                                <h2>
                                    <WarningOutlined /> Pénalités de Cession
                                </h2>
                                <p>Configuration des tranches de pénalités en fonction du nombre de jours de retard</p>
                            </div>

                            {/* Statistiques */}
                            <div className="stats-card">
                                <Row gutter={16}>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Total Tranches"
                                            value={stats.totalTranches}
                                            prefix={<ClockCircleOutlined />}
                                            valueStyle={{ color: '#ff4d4f' }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Montant Max/Jour"
                                            value={stats.montantMax}
                                            suffix="FCFA"
                                            // prefix={<DollarOutlined />}
                                            valueStyle={{ color: '#faad14' }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Montant Min/Jour"
                                            value={stats.montantMin}
                                            suffix="FCFA"
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Durée Max"
                                            value={stats.joursMax}
                                            suffix="jours"
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            {/* Actions */}
                            <div className="action-card">
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Alert
                                            message="Information"
                                            description="Les tranches définissent le montant de pénalité par jour selon le retard de paiement"
                                            type="info"
                                            showIcon
                                        />
                                    </Col>
                                    <Col>
                                        <Space>
                                            <Button
                                                icon={<ReloadOutlined />}
                                                onClick={chargerPenalites}
                                                loading={loading}
                                            >
                                                Actualiser
                                            </Button>
                                            {isPoolUser && (
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={ouvrirModalCreation}
                                                    style={{ background: '#ff4d4f', borderColor: '#ff4d4f' }}
                                                >
                                                    Nouvelle tranche
                                                </Button>
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </div>

                            {/* Table */}
                            <Card
                                title="Tranches de pénalités"
                                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
                            >
                                <ConfigProvider locale={frFR}>
                                    <Table
                                        columns={colonnes}
                                        dataSource={penalites}
                                        loading={loading}
                                        rowKey="penaliteId"
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total: ${total} tranche(s)`,
                                        }}
                                        scroll={{ x: 1000 }}
                                    />
                                </ConfigProvider>
                            </Card>

                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal de création/édition */}
            <Modal
                title={
                    <span>
                        <WarningOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
                        {editingPenalite ? 'Modifier la tranche de pénalité' : 'Nouvelle tranche de pénalité'}
                    </span>
                }
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Alert
                    message="Configuration de la tranche"
                    description="Définissez la période (en jours de retard) et le montant de pénalité appliqué par jour. Les tranches ne doivent pas se chevaucher."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={validerFormulaire}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Jours de début"
                                name="joursDebut"
                                rules={[
                                    { required: true, message: 'Requis' },
                                    { type: 'number', min: 0, message: 'Doit être ≥ 0' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Ex: 0"
                                    min={0}
                                    addonAfter="jours"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Jours de fin"
                                name="joursFin"
                                rules={[
                                    { required: true, message: 'Requis' },
                                    { type: 'number', min: 1, message: 'Doit être > 0' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const debut = getFieldValue('joursDebut');
                                            if (!value || !debut || value > debut) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Doit être > jours de début'));
                                        },
                                    }),
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Ex: 5"
                                    min={1}
                                    addonAfter="jours"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Montant de pénalité par jour"
                        name="montantParJour"
                        rules={[
                            { required: true, message: 'Requis' },
                            { type: 'number', min: 0, message: 'Doit être ≥ 0' }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Ex: 1000"
                            min={0}
                            step={100}
                            addonAfter="FCFA"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                            parser={value => value.replace(/\s?/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Description (optionnel)"
                        name="description"
                    >
                        <TextArea
                            rows={3}
                            placeholder="Exemple: Première tranche de retard - pénalité légère"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => {
                                setModalVisible(false);
                                form.resetFields();
                            }}>
                                Annuler
                            </Button>
                            <Button type="primary" htmlType="submit" danger>
                                {editingPenalite ? 'Modifier' : 'Créer'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default PenalitesCession;